import { Server } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { EmptyState, ErrorState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { apiErrorMessage } from '@/i18n'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtRelative } from '../shared/format'
import { useCreateInstallation, useInstallations } from './api'

export function InstallationsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const list = useInstallations()
  const create = useCreateInstallation()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    account_id: '',
    entitlement_id: '',
    name: '',
    environment: 'prod',
    hosting: 'musteri',
  })

  const { pageRows, pager } = usePagination(list.data ?? [])
  return (
    <>
      <PageHeader
        title={t('installations.title')}
        description={t('installations.subtitle')}
        actions={
          can('installations.manage') ? (
            <Button onClick={() => setOpen(true)}>{t('installations.new')}</Button>
          ) : null
        }
      />
      <Card>
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.isLoading ? (
          <div className="p-4">
            <Skeleton className="h-40" />
          </div>
        ) : (list.data?.length ?? 0) === 0 ? (
          <EmptyState />
        ) : (
          <>
            <Table>
              <THead>
                <TR className="hover:bg-transparent">
                  <TH>{t('installations.columns.name')}</TH>
                  <TH>{t('installations.columns.account')}</TH>
                  <TH>{t('installations.columns.health')}</TH>
                  <TH className="text-right">{t('installations.columns.seen')}</TH>
                </TR>
              </THead>
              <TBody>
                {pageRows.map((i) => (
                  <TR key={i.id}>
                    <TD className="font-medium">
                      <Link to={`/installations/${i.id}`} className="hover:underline">
                        {i.name}
                      </Link>
                    </TD>
                    <TD>
                      <Link to={`/accounts/${i.account_id}`} className="hover:underline">
                        {i.account_title}
                      </Link>
                    </TD>
                    <TD>
                      <Badge tone={i.license_state === 'licensed' ? 'success' : 'danger'}>{i.license_state || i.status}</Badge>
                    </TD>
                    <TD className="text-right text-muted-foreground">{i.last_heartbeat_at ? fmtRelative(i.last_heartbeat_at) : '—'}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <Pager {...pager} />
          </>
        )}
      </Card>
      <Modal
        open={open}
        onOpenChange={setOpen}
        icon={<Server />}
        title={t('installations.new')}
        onSubmit={(e) => {
          e.preventDefault()
          void create
            .mutateAsync(form)
            .then(() => {
              toast.success(t('common.saved'))
              setOpen(false)
            })
            .catch((err) => toast.error(apiErrorMessage(err)))
        }}
        footer={<Button type="submit">{t('common.save')}</Button>}
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>{t('installations.form.accountId')}</Label>
            <Input value={form.account_id} onChange={(e) => setForm((f) => ({ ...f, account_id: e.target.value }))} required />
          </div>
          <div className="space-y-1">
            <Label>{t('installations.form.entitlementId')}</Label>
            <Input value={form.entitlement_id} onChange={(e) => setForm((f) => ({ ...f, entitlement_id: e.target.value }))} required />
          </div>
          <div className="space-y-1">
            <Label>{t('installations.columns.name')}</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          </div>
          <div className="space-y-1">
            <Label>{t('installations.columns.environment')}</Label>
            <Input value={form.environment} onChange={(e) => setForm((f) => ({ ...f, environment: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label>{t('installations.form.hosting')}</Label>
            <Input value={form.hosting} onChange={(e) => setForm((f) => ({ ...f, hosting: e.target.value }))} />
          </div>
        </div>
      </Modal>
    </>
  )
}
